import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  actionTypes,
} from '../actions';
import {
  getProfile,
  getResourceIds,
  getResourceById,
} from '../selectors';
import {
  throwError,
} from './helpers';
import api from '../api';

export default function* documentFlow(reportChannel) {
  function* fetchDocuments({ userId, listName }) {
    const actions = createActionCreators('read', {
      resourceType: 'documents',
      list: listName || 'library',
      requestKey: 'fetchDocuments',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const profile = yield eff.select(
        getProfile,
      );

      const documents = yield eff.call(
        api.getPersonalEntity,
        {
          userId: userId || profile.id,
          type: profile.is_pro ? 'own_documents' : 'assigned_documents',
        },
      );
      yield eff.put(actions.succeeded({
        resources: documents,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* fetchDocument({
    id,
    withAssignees,
    callbacks,
  }) {
    const actions = createActionCreators('read', {
      resourceType: 'documents',
      list: 'library',
      requestKey: 'fetchDocument',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const document = yield eff.call(
        api.getEntity,
        {
          id,
          type: 'documents',
        },
      );
      let assignees = [];
      let groupAssignments = [];

      if (withAssignees) {
        assignees = yield eff.call(
          api.getEntityInternals,
          {
            id,
            type: 'documents',
            endpoint: 'assignments',
          },
        );
        groupAssignments = yield eff.call(
          api.getEntityInternals,
          {
            id,
            type: 'documents',
            endpoint: 'assignments/client_group_assignments',
          },
        );
      }

      yield eff.put(actions.succeeded({
        resources: [document],
        includedResources: {
          assignees,
          groupAssignments,
        },
        customLists: {
          assignees: `document_${id}`,
          groupAssignments: `document_${id}`,
        },
      }));
      callbacks?.onSuccess?.(document);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* assignDocumentToClient({
    id,
    list,
    payload,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'assignees',
      list: list || `document_${id}`,
      requestKey: 'assignDocumentToClient',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const result = yield eff.call(
        api.createAssignment,
        {
          id,
          type: 'documents',
          body: payload,
        },
      );

      const listName = `client_${payload.user_id}`;
      const ids = yield eff.select(
        getResourceIds('documents', listName),
      );
      const document = yield eff.select(
        getResourceById('documents', id),
      );

      yield eff.put(actions.succeeded({
        resources: [result],
        multiLists: {
          documents: {
            [listName]: ids ? [...ids, id] : [id],
          },
        },
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client.assigned',
            params: {
              type: 'Document',
            },
          },
        },
      );

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'assigned_document',
        attrs: {
          name: document.name,
        },
      });
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* unAssignDocumentToClient({
    id,
    clientId,
    callbacks,
    ...rest
  }) {
    let { assignmentId } = rest;
    const actions = createActionCreators('delete', {
      resourceType: 'assignees',
      requestKey: 'unAssignDocumentToClient',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      let multiLists = {};
      if (!assignmentId) {
        const assignees = yield eff.call(
          api.getEntityInternals,
          {
            id,
            type: 'documents',
            endpoint: 'assignments',
          },
        );
        assignmentId = assignees.find(a => a.user_id === clientId)?.id;

        const listName = `client_${clientId || id}`;
        const ids = yield eff.select(
          getResourceIds('documents', listName),
        );
        multiLists = {
          documents: {
            [listName]: ids.filter(docId => Number(docId) !== id),
          },
        };
      }

      const document = yield eff.call(
        api.deleteAssignment,
        {
          id,
          type: 'documents',
          endpoint: assignmentId,
        },
      );

      if (callbacks?.onSuccess) callbacks.onSuccess();

      yield eff.put(actions.succeeded({
        resources: [document],
        multiLists,
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client.unassigned',
            params: {
              type: 'Document',
            },
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      if (callbacks?.onError) callbacks.onError(e);

      yield eff.call(throwError, e);
    }
  }

  function* assignDocumentToGroup({
    id,
    list,
    payload,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      list: list || `document_${id}`,
      requestKey: 'assignDocumentToGroup',
      resourceType: 'groupAssignments',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const documents = yield eff.call(
        api.createAssignment,
        {
          id,
          type: 'client_groups',
          body: payload,
          endpoint: 'documents',
        },
      );

      if (callbacks?.onSuccess) callbacks.onSuccess();

      yield eff.put(actions.succeeded({
        resources: [{
          id,
          clients_count: documents.length,
        }],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client_group.assigned',
            params: {
              type: 'Document',
            },
          },
        },
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      if (callbacks?.onError) callbacks.onError(e);

      yield eff.call(throwError, e);
    }
  }

  function* unAssignDocumentToGroup({
    id,
    qs,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      id,
      resourceType: 'groupAssignments',
      requestKey: 'unAssignDocumentToGroup',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const result = yield eff.call(
        api.deleteAssignment,
        {
          id,
          type: 'client_groups',
          endpoint: 'documents',
          qs,
        },
      );

      if (callbacks?.onSuccess) callbacks.onSuccess(result);

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.client_group.unassigned',
            params: {
              type: 'Document',
            },
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [result],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      if (callbacks?.onError) callbacks.onError(e);

      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_DOCUMENTS, fetchDocuments);
  yield eff.takeEvery(actionTypes.REQUEST_GET_DOCUMENT, fetchDocument);

  yield eff.takeEvery(actionTypes.REQUEST_ASSIGN_DOCUMENT_TO_CLIENT, assignDocumentToClient);
  yield eff.takeEvery(actionTypes.REQUEST_UNASSIGN_DOCUMENT_TO_CLIENT, unAssignDocumentToClient);
  yield eff.takeEvery(actionTypes.REQUEST_ASSIGN_DOCUMENT_TO_GROUP, assignDocumentToGroup);
  yield eff.takeEvery(actionTypes.REQUEST_UNASSIGN_DOCUMENT_TO_GROUP, unAssignDocumentToGroup);
}
